import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { type Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { dashboardUrl } from '@shared/domain/dashboard-urls';
import type { DftBannerType } from '@widgets/banners/models/banners.model';

import type {
  DashboardAssessmentSummary,
  DashboardOversightSummaryBean,
  DashboardVPSummary,
  IBasicChartConfigurations,
  INotification,
} from '../models';

interface DashboardDataService {
  /**
   * Retrieves the Company profile data for the oversight chart. This info goes first,
   *
   */
  getCompanyProfileSummary(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the Questionnaire data for the oversight chart.
   */
  getDDQSummary(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the KYC data for the oversight chart.
   */
  getKYC(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the KPI data for the oversight chart.
   */
  getKPI(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the Reporting data for the oversight chart.
   */
  getReportingDashboardData(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the PM KPI data for the my profile chart.
   */
  getPmKpiMyProfile(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the Policy Files data for the oversight chart.
   */
  getPolicy(): Observable<IBasicChartConfigurations>;

  /**
   * Retrieves the Investor area data for the oversight chart.
   */
  getInvestorProfileSummary(): Observable<IBasicChartConfigurations>;

  /**
   *  Retrieves the data for the summary chart.
   *
   * @param [guid]
   */
  getOversightSummary(guid?: string): Observable<DashboardOversightSummaryBean>;

  /**
   *  Retrieves the data for the summary chart.
   *
   * @param [companyId]
   */
  getAssessmentSummary(companyId?: string): Observable<DashboardAssessmentSummary>;

  /**
   *  Retrieves the data for vp selected for the assessment chart.
   *
   * @param [subAccountId]
   */
  getVPSummary(subAccountId?: string): Observable<DashboardVPSummary>;

  /**
   * Retrieves the UK Consumer-duty data for banner.
   */
  getUkcdBanner(): Observable<DftBannerType[]>;

  /**
   * Retrieves user Notifications.
   */
  getNotifications(): Observable<INotification>;
}

@Service()
export class DashboardService implements DashboardDataService {
  private readonly _http = inject(HttpClient);

  getCompanyProfileSummary(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.companySummary());
  }

  getDDQSummary(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.ddqSummary());
  }

  getKYC(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.kycMyProfile());
  }

  getKPI(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.kpiMyProfile());
  }

  getPolicy(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.policyMyProfile());
  }

  getInvestorProfileSummary(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.investorProfile());
  }

  getReportingDashboardData(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.reportingMyProfile());
  }

  getPmKpiMyProfile(): Observable<IBasicChartConfigurations> {
    return this._http.get<IBasicChartConfigurations>(dashboardUrl.pmKpiMyProfile());
  }

  getOversightSummary(guid?: string): Observable<DashboardOversightSummaryBean> {
    const params: HttpParams = guid ? new HttpParams().set('subAccount', guid) : new HttpParams();

    return this._http.get<DashboardOversightSummaryBean>(dashboardUrl.oversightSummary(), {
      params,
    });
  }

  getAssessmentSummary(companyId?: string): Observable<DashboardAssessmentSummary> {
    const params: HttpParams = companyId
      ? new HttpParams().set('companyId', companyId.toString())
      : new HttpParams();

    return this._http.get<DashboardAssessmentSummary>(dashboardUrl.assessmentSummary(), {
      params,
    });
  }

  getVPSummary(subAccountId?: string): Observable<DashboardVPSummary> {
    const params: HttpParams = subAccountId
      ? new HttpParams().set('subAccount', subAccountId)
      : new HttpParams();

    return this._http.get<DashboardVPSummary>(dashboardUrl.vpSummary(), { params });
  }

  getUkcdBanner(): Observable<DftBannerType[]> {
    return this._http.get<DftBannerType[]>(dashboardUrl.ukcdBanner());
  }

  getNotifications(): Observable<INotification> {
    return this._http.get<INotification>(dashboardUrl.notifications());
  }
}

export const ukcdBannerResolver: ResolveFn<DftBannerType[]> = () => {
  return inject(DashboardService)
    .getUkcdBanner()
    .pipe(catchError(() => of([])));
};
